import { Pop } from '@bz/ui';
import { useNavigate } from '@solidjs/router';
import { For, Match, Show, Switch } from 'solid-js';

import img_safe from '../../../../../assets/safe.png';
import CopyInput from '../../../../../components/ui/CopyInput';
import LabelItem from '../../../../../components/ui/LabelItem';
import Notice from '../../../../../components/ui/Notice';
import Qrcode from '../../../../../components/ui/Qrcode';
import { DepositMethod } from '../../../../../types';
import { getThemeDarken, onCopy } from '../../../../../utils';
import UtrTips from '../notices/Utr';
interface BankCardProps {
  cardDetails: string;
}

interface TopTipsProps {
  isKes: boolean;
  isSunV2Pay: boolean;
  isPkr: boolean;
  isEcp: boolean;
  showUtr: boolean;
}

interface UtrProps {
  method: DepositMethod;
  amount: number;
  currency: string;
  orderDetails: ApiResponse<'createWalletCashierDeposit'>;
}

function BankCard({ cardDetails }: BankCardProps) {
  const details = cardDetails.split(',');

  return (
    <Show
      when={details.length > 1}
      fallback={
        <>
          <div class="mb-1 text-secondary mt-5">{t('Bank Account')}</div>
          <CopyInput value={cardDetails} />
        </>
      }
    >
      <For each={details}>{(item) => <div>{item}</div>}</For>
    </Show>
  );
}

function truncateCode(code: string): string {
  if (code.length < 20) return code;
  return `${code.slice(0, 10)}...${code.slice(-10)}`;
}

function PaymentQrCode({ qrCodeUrl, qrCodeContent }: { qrCodeUrl: string | null; qrCodeContent: string | null }) {
  const url = qrCodeUrl || qrCodeContent || '';

  return (
    <>
      <Show when={url}>{(res) => <Qrcode url={res()} />}</Show>
      <Show when={qrCodeContent}>
        {(res) => (
          <div class="flex justify-between items-center mt-4">
            <div>{t('Code')}</div>
            <div class="text-secondary items-center flex">
              {truncateCode(res())}
              <button class="ml-1" onClick={() => onCopy(res())}>
                <IconCopy />
              </button>
            </div>
          </div>
        )}
      </Show>
    </>
  );
}

function PaymentTopTips({ isKes, isSunV2Pay, showUtr, isPkr, isEcp }: TopTipsProps) {
  return (
    <Switch
      fallback={
        <span
          innerHTML={t(
            'Please transfer funds within <b>__time__ minutes</b> using the payment details specified below. Then enter the <b>__txt__</b> below after your transfer. Otherwise, your deposit will not be credited on time.',
            { time: isEcp ? 20 : 10, txt: isEcp ? 'TX number' : 'UTR number' }
          )}
        />
      }
    >
      <Match when={isKes}>
        {t(
          'A confirmation pop-up should be sent to you by the mobile network operator, please confirm your transaction as soon as possible.'
        )}
      </Match>
      <Match when={isSunV2Pay}>
        <div>
          <div>{t('Please transfer funds within 10 minutes using the payment details specified below.')}</div>
          <ul>
            <li>
              {t(
                'Please refrain from storing your iBank account for future deposits. When making transfers, kindly create an order using the appropriate bank transfer method. SWIFT is not supported by the current transaction.'
              )}
            </li>
            <li>{t('It is essential to provide reference information to ensure immediate fund allocation.')}</li>
          </ul>
        </div>
      </Match>
      <Match when={!showUtr}>
        <span
          innerHTML={t('Please transfer funds within <b>10 minutes</b> using the payment details specified below.')}
        ></span>
      </Match>
      <Match when={isPkr}>
        <span
          innerHTML={t(
            'Please transfer funds within <b>10 minutes</b> using the payment details specified below. Enter the <b>OTP code</b> below after your transfer, and you will see an <b>MPIN</b> pop-up to approve this transaction, or you may have to check in-app approval(s) if you don’t see <b>MPIN</b> pop-up. Otherwise, your transaction may fail.'
          )}
        ></span>
      </Match>
    </Switch>
  );
}

function ConfirmationPanel(props: UtrProps) {
  const { currency, amount, orderDetails, method } = props;
  const { method: methodName, orderId, payInfo, channel, fiatProvider } = orderDetails;
  const { qrCode, qrCodeContent, card, payList } = payInfo;
  const showMadeDeposit = method.mode === 4;
  const isKes = methodName === 'MPesa' && channel === 'MPesa_Express';
  const isSunV2Pay = methodName === 'SunV2Pay';
  const isPkr = currency === 'PKRFIAT';
  const isEcp = fiatProvider === 'EcPay';
  const navigate = useNavigate();
  const navigateToDetails = (id: string) => {
    pop.pop();
    navigate(`/deposit-detail/?orderId=${id.replace('F-', '')}`);
  };

  const confirmPayment = async () => {
    try {
      // await getHttp().post('/payment/deposit/fiat/order/paymentConfirmation/', { orderId });
      navigateToDetails(orderId);
    } catch (err: any) {
      toast.error(err);
    }
  };
  return (
    <Pop title={t('Payment Method')}>
      <div class="space-y-4">
        <img src={img_safe} class="mx-auto size-20" />
        <div class="flex items-center bg-layer4 p-3 text-right">
          <img src={getThemeDarken() ? props.method.nightIconUrl : props.method.lightIconUrl} class="h-10" />
          <div class="ml-auto">
            <div>{t('Payment Method')}</div>
            <div class="text-neutral-main1">
              {amount} {currency}
            </div>
          </div>
        </div>

        <Notice type="brand">
          <PaymentTopTips isKes={isKes} isSunV2Pay={isSunV2Pay} showUtr={false} isPkr={isPkr} isEcp={isEcp} />
        </Notice>
        <Show when={payList || qrCode || qrCodeContent}>
          <div class="rounded-xl bg-layer4 p-3 space-y-4">
            <Show when={payList}>
              <For each={JSON.parse(String(payList))}>
                {(item) => {
                  const key = Object.keys(item)[0];
                  const value = item[key];
                  return <LabelItem label={key} value={value} class="" />;
                }}
              </For>
            </Show>
            <PaymentQrCode qrCodeUrl={qrCode} qrCodeContent={qrCodeContent} />
          </div>
        </Show>
        {card && (
          <div class="deposit-details-section">
            <BankCard cardDetails={card} />
          </div>
        )}
        <UtrTips currency={currency} channel={channel} />
        <Button
          class="w-full my-4"
          onClick={showMadeDeposit ? confirmPayment : () => navigateToDetails(orderId)}
          type="second"
        >
          {t('I’ve Made Deposit')}
        </Button>
      </div>
    </Pop>
  );
}

function openConfirmationPanel(props: UtrProps) {
  pop.push(() => <ConfirmationPanel {...props} />);
}

export default openConfirmationPanel;
